import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import './VerifyGiveaway.css';

const VerifyGiveaway = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [verificationData, setVerificationData] = useState(null);
  const [message, setMessage] = useState('');

  const verifyGiveaway = useCallback(async () => {
    try {
      const id = searchParams.get('id');
      if (!id) {
        setStatus('error');
        setMessage('No giveaway ID provided. Please check the link.');
        return;
      }

      // First try regular giveaway verification
      try {
        const response = await axios.get(`/api/giveaways/verify/${id}`);
        setVerificationData({ ...response.data, type: 'giveaway' });
        setStatus('success');
        return;
      } catch (giveawayError) {
        if (giveawayError.response?.status !== 404) {
          throw giveawayError; // Re-throw if it's not a 404
        }
        // If 404, try Twitter verification
      }

      // Try Twitter giveaway roll verification
      try {
        const response = await axios.get(`/api/twitter-verification/verify/${id}`);
        if (response.data.success) {
          setVerificationData({ ...response.data.data, type: 'twitter' });
          setStatus('success');
        } else {
          throw new Error(response.data.message || 'Verification failed');
        }
      } catch (twitterError) {
        setStatus('error');
        if (twitterError.response?.status === 404) {
          setMessage('Giveaway or Twitter roll not found. The giveaway may have been deleted or the ID is incorrect.');
        } else {
          setMessage(twitterError.response?.data?.message || 'Failed to verify giveaway. Please try again later.');
        }
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.message || 'Failed to verify giveaway. Please try again later.');
    }
  }, [searchParams]);

  useEffect(() => {
    verifyGiveaway();
  }, [verifyGiveaway]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="verify-giveaway-container">
      <div className="verify-giveaway-card">
        {status === 'loading' && (
          <>
            <div className="spinner"></div>
            <h2>Verifying Giveaway...</h2>
            <p>Please wait while we fetch the giveaway verification details.</p>
          </>
        )}

        {status === 'success' && verificationData && (
          <>
            <div className="success-icon">✓</div>
            <h2>{verificationData.type === 'twitter' ? 'Twitter Giveaway Verification' : 'Giveaway Verification'}</h2>

            <div className="verification-details">
              <div className="detail-row">
                <span className="label">{verificationData.type === 'twitter' ? 'Verification ID' : 'Giveaway ID'}:</span>
                <span className="value">{verificationData.type === 'twitter' ? verificationData.verificationId : verificationData.giveawayId}</span>
              </div>

              {verificationData.type === 'giveaway' ? (
                <>
                  <div className="detail-row">
                    <span className="label">Title:</span>
                    <span className="value">{verificationData.title}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Host:</span>
                    <span className="value">{verificationData.host}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Total Participants:</span>
                    <span className="value">{verificationData.totalParticipants}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Status:</span>
                    <span className={`value status-${verificationData.status.toLowerCase()}`}>
                      {verificationData.status.charAt(0).toUpperCase() + verificationData.status.slice(1)}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">End Date:</span>
                    <span className="value">{formatDate(verificationData.endDate)}</span>
                  </div>

                  {verificationData.currentWinner && (
                    <div className="detail-row winner-row">
                      <span className="label">Current Winner:</span>
                      <span className="value winner">{verificationData.currentWinner}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="detail-row">
                    <span className="label">Tweet ID:</span>
                    <span className="value">{verificationData.tweetId}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Rolled By:</span>
                    <span className="value">{verificationData.rolledBy}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Total Retweeters:</span>
                    <span className="value">{verificationData.totalRetweeters}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Number of Winners:</span>
                    <span className="value">{verificationData.numberOfWinners}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Credits Used:</span>
                    <span className="value">{verificationData.creditsUsed}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Rolled At:</span>
                    <span className="value">{formatDate(verificationData.rolledAt)}</span>
                  </div>

                  {verificationData.fromCache && (
                    <div className="detail-row">
                      <span className="label">Data Source:</span>
                      <span className="value">Cached (No additional credits charged)</span>
                    </div>
                  )}

                  {verificationData.rerollReason && (
                    <div className="detail-row">
                      <span className="label">Reroll Reason:</span>
                      <span className="value">{verificationData.rerollReason}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {verificationData.type === 'giveaway' && verificationData.rollLogs && verificationData.rollLogs.length > 0 && (
              <div className="roll-history">
                <h3>Roll History</h3>
                <div className="roll-logs">
                  {verificationData.rollLogs.map((log, index) => (
                    <div key={index} className="roll-log">
                      <div className="roll-header">
                        <span className="roll-type">{log.rollType}</span>
                        {log.isReroll && <span className="reroll-badge">Reroll</span>}
                      </div>
                      <div className="roll-details">
                        <div className="detail-row">
                          <span className="label">Winner:</span>
                          <span className="value winner">{log.winner}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Participants:</span>
                          <span className="value">{log.totalParticipants}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Rolled:</span>
                          <span className="value">{formatDate(log.timestamp)}</span>
                        </div>
                        {log.rerollReason && (
                          <div className="detail-row">
                            <span className="label">Reason:</span>
                            <span className="value">{log.rerollReason}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {verificationData.type === 'twitter' && verificationData.winners && verificationData.winners.length > 0 && (
              <div className="winners-section">
                <h3>Winners</h3>
                <div className="winners-list">
                  {verificationData.winners.map((winner, index) => (
                    <div key={index} className="winner-card">
                      <div className="winner-header">
                        <span className="winner-number">Winner #{index + 1}</span>
                      </div>
                      <div className="winner-details">
                        <div className="detail-row">
                          <span className="label">Username:</span>
                          <span className="value">@{winner.username}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Display Name:</span>
                          <span className="value">{winner.displayName}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Followers:</span>
                          <span className="value">{winner.followers?.toLocaleString() || 'N/A'}</span>
                        </div>
                        {winner.isVerified && (
                          <div className="detail-row">
                            <span className="label">Verified:</span>
                            <span className="value">✓ Yes</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {verificationData.type === 'twitter' && verificationData.rollHistory && verificationData.rollHistory.length > 1 && (
              <div className="roll-history">
                <h3>Roll History for this Tweet</h3>
                <div className="roll-logs">
                  {verificationData.rollHistory.map((roll) => (
                    <div key={roll.verificationId} className={`roll-log ${roll.isCurrentRoll ? 'current-roll' : ''}`}>
                      <div className="roll-header">
                        <span className="roll-type">Roll #{roll.rollNumber}</span>
                        {roll.isCurrentRoll && <span className="current-badge">Current</span>}
                      </div>
                      <div className="roll-details">
                        <div className="detail-row">
                          <span className="label">Verification ID:</span>
                          <span className="value">{roll.verificationId}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Rolled By:</span>
                          <span className="value">{roll.rolledBy}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Winners:</span>
                          <span className="value">{roll.numberOfWinners}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Credits Used:</span>
                          <span className="value">{roll.creditsUsed}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Rolled:</span>
                          <span className="value">{formatDate(roll.rolledAt)}</span>
                        </div>
                        {roll.fromCache && (
                          <div className="detail-row">
                            <span className="label">Data Source:</span>
                            <span className="value">Cached</span>
                          </div>
                        )}
                        {roll.rerollReason && (
                          <div className="detail-row">
                            <span className="label">Reason:</span>
                            <span className="value">{roll.rerollReason}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="verification-footer">
              <p>This verification page shows the official results of {verificationData.type === 'twitter' ? 'this Twitter giveaway roll' : 'this giveaway roll'}.</p>
              <Link to={verificationData.type === 'twitter' ? "/twitter-winner-picker" : "/giveaways"} className="btn-primary">
                {verificationData.type === 'twitter' ? 'Go to Twitter Winner Picker' : 'View All Giveaways'}
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="error-icon">✗</div>
            <h2>Verification Failed</h2>
            <p>{message}</p>
            <div className="action-buttons">
              <Link to="/giveaways" className="btn-primary">
                Browse Giveaways
              </Link>
              <Link to="/" className="btn-secondary">
                Go Home
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyGiveaway;